import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import moment from 'moment'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import getBedbankPromoExtraValue, { getDisplayTotalPricePerNight, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { safeDivideAndCeil } from 'lib/maths/mathUtils'
import { shouldShowPayAtPropertyPill, shouldShowPayLaterPill } from '../../utils'
import BedbankSummaryPriceDetailsView from './BedbankSummaryPriceDetailsView'

interface Props {
  /**
   * rate here is lowest rate within the offer
   */
  rate?: App.BedbankRate
  bundleRate?: App.BedbankRate
  rooms: Array<App.Occupants>
  offer: App.BedbankOffer
  flightOrigin?: App.Airport
  flightPrice?: number
  duration: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled: boolean
  onViewPackageOptions?: () => void
  promotion?: boolean
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
  priceTrackedRef?: React.RefObject<boolean>
  isFlightsSelected?: boolean
}

function BedbankSummaryPriceDetailsSameRoom(props: Props) {
  const {
    rate,
    bundleRate,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    promotion = false,
    alignment = 'center',
    luxPlusLPPEnabled,
    priceTrackedRef,
    isFlightsSelected,
  } = props

  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const hasFlights = !!flightPrice
  const sell = useBedbankOfferSell(offer, duration, rooms)

  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])
  const promo = useBedbankRoomRatePromotions(offer.promotions, checkIn, checkOut)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const taxesAndFees = rate?.totals?.taxesAndFees ?? sell?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

  const hotelPrice = rate ? rate.totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = rate?.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  let hotelValue: undefined | number
  let bundleValue: undefined | number

  let hotelDiscount: number
  let hotelMemberDiscount: number = 0

  if (rate) {
    const pkg = rate.room
    const isPromoEnabledRate = !!promo[rate.promotionId ?? '']

    if (isPromoEnabledRate) {
      const inclusionsAmount = pkg?.sell ? getBedbankPromoExtraValue({
        price: pkg.sell.price,
        value: pkg.sell.value,
        minLos: pkg.sell.los,
        duration,
      }) : 0
      hotelValue = inclusionsAmount + rate.totals.inclusive
      bundleValue = inclusionsAmount + (bundleRate?.totals.inclusive || hotelValue)
      hotelDiscount = getValueOffPercent(hotelValue, inclusionsAmount)
    } else {
      hotelValue = rate.value
      bundleValue = bundleRate?.value || hotelValue
      hotelDiscount = rate.discount
      hotelMemberDiscount = rate.memberDiscount
    }
  } else {
    hotelValue = sell?.value
    bundleValue = sell?.value
    hotelDiscount = sell?.discountPercentage ?? 0
  }

  const hotelNights = rate?.nights ?? sell?.los

  const departureTooFarInFuture = useMemo(() => checkIn ? moment(checkIn).diff(moment.utc(), 'days') > 330 : false, [checkIn])

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const baseTotalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    hotelDiscount,
    hotelMemberDiscount,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPricesPerNight = getDisplayTotalPricePerNight(baseTotalPrices, hotelNights || 1)
  const showOneNightPrice = !promotion && !offer.displayPricingAsPerPerson && !checkIn && !!hotelNights && hotelNights > 1
  const totalPrices = showOneNightPrice ? totalPricesPerNight : baseTotalPrices
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const discount = promoteAsBundle ? totalPrices.original.discountWithFlight : totalPrices.original.discount
  const memberDiscount = promoteAsBundle ? totalPrices.original.memberDiscountWithFlight : totalPrices.original.memberDiscount
  const displayDiscount = useBedbankDiscount(discount, memberDiscount)

  useEffect(() => {
    appDispatch(setCustomerValue(totalPrices.totals.price))
  }, [appDispatch, totalPrices.totals.price])
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: totalPrices.totals.price,
      memberPrice: totalPrices.totals.memberPrice,
      propertyFees: showOneNightPrice ? safeDivideAndCeil(propertyFees, hotelNights || 1) : propertyFees,
      propertyId: offer.property.id,
      offerId: offer.id,
      propertyCountryCode: offer.property.address.countryCode,
      currentRegionCode,
    }),
  ], [rate, totalPrices.totals.price, totalPrices.totals.memberPrice, showOneNightPrice, propertyFees, hotelNights, offer, currentRegionCode])

  const showHotelPriceWithFlights = !!flightsEnabled && hasFlights && !offer.promoteAsBundle
  const priceToTrack = showHotelPriceWithFlights ?
    totalPrices?.totals?.price :
    promoteAsBundle ? totalPrices?.totals?.flightBundlePrice : totalPrices?.totals?.price

  const shouldTrackPrice = !!(showHotelPriceWithFlights ? totalPrices?.totals?.price : totalPrices?.totals?.flightBundlePrice || totalPrices?.totals?.price)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()

  useEffect(() => {
    if (shouldTrackPrice && !!priceToTrack && searchId && offer?.id && priceTrackedRef && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: priceToTrack.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, priceToTrack, offer?.id])

  const canPayLater = !!rate && shouldShowPayLaterPill(rate)
  const showPayAtPropertyPill = !isFlightsSelected && !!rate && shouldShowPayAtPropertyPill(rate)

  return <BedbankSummaryPriceDetailsView
    rooms={rooms}
    offer={offer}
    flightOrigin={flightOrigin}
    flightPrice={flightPrice}
    duration={duration}
    departureTooEarly={departureTooEarly}
    flightsEnabled={flightsEnabled}
    onViewPackageOptions={onViewPackageOptions}
    onToggleFilter={onToggleFilter}
    alignment={alignment}
    luxPlusLPPEnabled={luxPlusLPPEnabled}
    showPayAtPropertyPill={showPayAtPropertyPill}
    showHotelPriceWithFlights={showHotelPriceWithFlights}
    canPayLater={canPayLater}
    displayDiscount={displayDiscount}
    nightsToDisplay={nightsToDisplay}
    totalPrices={totalPrices}
    saleUnit={saleUnit}
    taxesAndFees={taxesAndFees}
    propertyFees={propertyFees}
    hotelPrice={hotelPrice}
    hotelMemberPrice={hotelMemberPrice}
    hotelValue={hotelValue}
    bundlePropertyFees={bundlePropertyFees}
    bundlePrice={bundlePrice}
    bundleMemberPrice={bundleMemberPrice}
    bundleTaxesAndFees={bundleTaxesAndFees}
    pointsEarnCalculationRequests={pointsEarnCalculationRequests}
    hasFlights={hasFlights}
    departureTooFarInFuture={departureTooFarInFuture}
    promoteAsBundle={promoteAsBundle}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
}), BedbankSummaryPriceDetailsSameRoom)
